import type { KeyboardEvent, ReactNode } from "react";

import { useT } from "../i18n";
import { Panel } from "./atoms";
import {
  CabecalhoDaTabela,
  CelulasDaLinha,
  ControlesDaTabela,
  useTabela,
  type Coluna,
} from "./tabela";

/**
 * A tabela de classificação, que existia duas vezes.
 *
 * Uma na carreira e outra na liga online, escritas separadas, com colunas
 * diferentes e estilos diferentes para a mesma coisa. Elas já tinham divergido:
 * quando a tabela do jogo ganhou coluna escolhível e busca para caber num
 * celular, só a da carreira ganhou, porque a outra era outro arquivo.
 *
 * Cada tela guarda a classificação no formato que o motor ou o servidor
 * devolve, e os dois formatos são diferentes. Por isso a entrada aqui é uma
 * linha normalizada: quem chama traduz o que tem, e a tabela deixa de precisar
 * saber de onde veio.
 */

export interface LinhaDaClassificacao {
  clubId: string;
  posicao: number;
  nome: string;
  /** A primeira cor do clube, para a listra que o identifica. Opcional. */
  cor?: string | undefined;
  jogos: number;
  vitorias: number;
  empates: number;
  derrotas: number;
  golsPro: number;
  golsContra: number;
  pontos: number;
  /** O clube de quem está olhando, destacado na linha. */
  minha: boolean;
  /** Dentro da faixa que sobe de divisão ou se classifica. */
  classifica?: boolean;
}

function useColunas(): Array<Coluna<LinhaDaClassificacao>> {
  const t = useT();

  return [
    {
      chave: "posicao",
      rotulo: t("competicoes.colunas.posicao"),
      presenca: "sempre",
      largura: 34,
      direita: true,
      ordenar: (linha) => linha.posicao,
      direcaoInicial: "asc",
      celula: (linha) => (
        <span className="num" style={{ color: "var(--text-dim)" }}>
          {linha.posicao}
        </span>
      ),
    },
    {
      chave: "clube",
      rotulo: t("competicoes.colunas.clube"),
      presenca: "sempre",
      ordenar: (linha) => linha.nome,
      direcaoInicial: "asc",
      celula: (linha) => (
        <span className="row" style={{ gap: 8 }}>
          {linha.cor && (
            <span
              className="fixo"
              style={{
                width: 4,
                height: 18,
                borderRadius: 2,
                background: linha.cor,
                display: "inline-block",
              }}
            />
          )}
          <span className="corta">{linha.nome}</span>
        </span>
      ),
    },
    /*
     * Pontos é a primeira de destaque, então é a que o celular abre mostrando.
     *
     * Numa classificação, a pergunta é quantos pontos cada um tem; jogos e
     * saldo vêm depois. Na ordem do computador ela continua por último, que é
     * onde se espera achá-la numa tabela de campeonato.
     */
    {
      chave: "pontos",
      rotulo: t("competicoes.colunas.pontos"),
      presenca: "destaque",
      largura: 44,
      direita: true,
      ordenar: (linha) => linha.pontos,
      celula: (linha) => (
        <span className="num" style={{ color: "var(--primary)", fontWeight: 700 }}>
          {linha.pontos}
        </span>
      ),
    },
    {
      chave: "jogos",
      rotulo: t("competicoes.colunas.jogos"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (linha) => linha.jogos,
      celula: (linha) => <span className="num">{linha.jogos}</span>,
    },
    {
      chave: "vitorias",
      rotulo: t("competicoes.colunas.vitorias"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (linha) => linha.vitorias,
      celula: (linha) => <span className="num">{linha.vitorias}</span>,
    },
    {
      chave: "empates",
      rotulo: t("competicoes.colunas.empates"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (linha) => linha.empates,
      celula: (linha) => <span className="num">{linha.empates}</span>,
    },
    {
      chave: "derrotas",
      rotulo: t("competicoes.colunas.derrotas"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (linha) => linha.derrotas,
      celula: (linha) => <span className="num">{linha.derrotas}</span>,
    },
    {
      chave: "golsPro",
      rotulo: t("competicoes.colunas.golsPro"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (linha) => linha.golsPro,
      celula: (linha) => <span className="num">{linha.golsPro}</span>,
    },
    {
      chave: "golsContra",
      rotulo: t("competicoes.colunas.golsContra"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (linha) => linha.golsContra,
      celula: (linha) => <span className="num">{linha.golsContra}</span>,
    },
    {
      chave: "saldo",
      rotulo: t("competicoes.colunas.saldo"),
      presenca: "destaque",
      largura: 44,
      direita: true,
      ordenar: (linha) => linha.golsPro - linha.golsContra,
      celula: (linha) => <span className="num">{linha.golsPro - linha.golsContra}</span>,
    },
  ];
}

export function Classificacao({
  titulo,
  acao,
  linhas,
  alturaMaxima,
  aoTocar,
}: {
  titulo: string;
  acao?: ReactNode;
  linhas: LinhaDaClassificacao[];
  alturaMaxima?: string | undefined;
  /** Tocar na linha de outro clube abre a ficha dele. */
  aoTocar?: ((clubId: string) => void) | undefined;
}): ReactNode {
  const tabela = useTabela<LinhaDaClassificacao>({
    colunas: useColunas(),
    procurar: (linha) => linha.nome,
    destaqueInicial: "pontos",
  });

  const prontas = tabela.preparar(linhas);

  return (
    <Panel title={titulo} action={acao} padded={false}>
      <ControlesDaTabela tabela={tabela} />
      <div className="scroll" style={alturaMaxima ? { maxHeight: alturaMaxima } : undefined}>
        <table className="fixa">
          <CabecalhoDaTabela tabela={tabela} />
          <tbody>
            {prontas.map((linha) => (
              <tr
                key={linha.clubId}
                className={aoTocar && !linha.minha ? "player linha-clicavel" : "player"}
                {...(aoTocar && !linha.minha
                  ? {
                      role: "button",
                      tabIndex: 0,
                      onClick: () => aoTocar(linha.clubId),
                      onKeyDown: (evento: KeyboardEvent<HTMLTableRowElement>) => {
                        if (evento.key !== "Enter" && evento.key !== " ") return;
                        evento.preventDefault();
                        aoTocar(linha.clubId);
                      },
                    }
                  : {})}
                style={{
                  background: linha.minha ? "var(--primary-glow)" : "transparent",
                  fontWeight: linha.minha ? 700 : 400,
                  // A faixa que sobe de divisão fica marcada na borda, e não na
                  // cor de fundo: o fundo já diz de quem é a linha.
                  borderLeft: linha.classifica
                    ? "2px solid var(--primary)"
                    : "2px solid transparent",
                }}
              >
                <CelulasDaLinha tabela={tabela} linha={linha} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
